import { useState, useMemo, useEffect } from 'react';
import { useI18n } from '../i18n/i18n';
import { LanguageLink } from '../components/LanguageLink';
import { useSEO } from '../hooks/useSEO';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, Film, LineChart, DollarSign, Lock, PiggyBank, Home, Search, Filter, X } from 'lucide-react';

const ResourcesPage = () => {
  const { t } = useI18n();

  // SEO Configuration
  const seoContent = useSEO('main', 'resources');
  
  const [activeTab, setActiveTab] = useState('articles');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [articlesToShow, setArticlesToShow] = useState(9);
  const [videosToShow, setVideosToShow] = useState(9);
  const [calculatorsToShow, setCalculatorsToShow] = useState(9);

  // Function to normalize text for flexible searching
  const normalizeText = (text: string): string => {
    return text
      .toLowerCase()
      .normalize('NFD') // Decompose accented characters
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics (tildes, accents)
      .replace(/[^\w\s]/g, ' ') // Replace punctuation with spaces
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim(); // Remove leading/trailing spaces
  };

  // Function to check if search term matches text with flexible but precise matching
  const matchesSearchTerm = (text: string, searchTerm: string): boolean => {
    if (!searchTerm.trim()) return true;
    
    const normalizedText = normalizeText(text);
    const normalizedSearch = normalizeText(searchTerm);
    
    // Split search term into words (minimum 2 characters)
    const searchWords = normalizedSearch.split(' ').filter(word => word.length >= 2);
    
    // If no valid search words, return true
    if (searchWords.length === 0) return true;
    
    // Check if all search words are found in the text
    return searchWords.every(word => normalizedText.includes(word));
  };

  // Articles data structure
  const articles = useMemo(() => [
    {
      id: 1,
      titleKey: 'home.info.title',
      descriptionKey: 'home.info.description',
      category: 'investment',
      imageUrl: '/images/articles/1554224155-6726b3ff858f',
      imageAlt: 'Compound interest concept',
      href: '/resources/compound-interest-article'
    },
    {
      id: 2,
      titleKey: 'resources.article2.title',
      descriptionKey: 'resources.article2.description',
      category: 'retirement',
      imageUrl: '/images/articles/1553729459-efe14ef6055d',
      imageAlt: 'Retirement planning',
      href: '/resources/retirement-planning-article'
    },
    {
      id: 3,
      titleKey: 'resources.article3.title',
      descriptionKey: 'resources.article3.description',
      category: 'taxPlanning',
      imageUrl: '/images/articles/1554224154-26032ffc0d07',
      imageAlt: 'Tax optimization strategies',
      href: '/resources/tax-optimization',
      isPremium: true
    },
    {
      id: 4,
      titleKey: 'resources.article4.title',
      descriptionKey: 'resources.article4.description',
      category: 'budgeting',
      imageUrl: '/images/articles/1633158829585-23ba8f7c8caf',
      imageAlt: 'Emergency fund concept',
      href: '/resources/emergency-fund-article'
    },
    {
      id: 5,
      titleKey: 'resources.article5.title',
      descriptionKey: 'resources.article5.description',
      category: 'investment',
      imageUrl: '/images/articles/1590283603385-17ffb3a7f29f',
      imageAlt: 'Risk vs return investment concept',
      href: '/resources/risk-return-article'
    },
    {
      id: 6,
      titleKey: 'resources.article6.title',
      descriptionKey: 'resources.article6.description',
      category: 'investment',
      imageUrl: '/images/articles/1611974789855-9c2a0a7236a3',
      imageAlt: 'Investing mistakes concept',
      href: '/resources/investing-mistakes-article'
    },
    {
      id: 7,
      titleKey: 'resources.article7.title',
      descriptionKey: 'resources.article7.description',
      category: 'mortgages',
      imageUrl: '/images/articles/1560518883-ce09059eeffa',
      imageAlt: 'Renting vs buying concept',
      href: '/resources/renting-vs-buying-article'
    },
    {
      id: 8,
      titleKey: 'resources.article8.title',
      descriptionKey: 'resources.article8.description',
      category: 'debt',
      imageUrl: '/images/articles/1434626881859-194d67b2b86f',
      imageAlt: 'Inflation impact concept',
      href: '/resources/inflation-article'
    },
    {
      id: 9,
      titleKey: 'resources.article9.title',
      descriptionKey: 'resources.article9.description',
      category: 'investment',
      imageUrl: '/images/articles/1518546305927-5a555bb7020d',
      imageAlt: 'Bitcoin logo and cryptocurrency concept',
      href: '/resources/crypto-article'
    },
    {
      id: 10,
      titleKey: 'resources.article10.title',
      descriptionKey: 'resources.article10.description',
      category: 'budgeting',
      imageUrl: '/images/articles/1579621970588-a35d0e7ab9b6',
      imageAlt: 'Monthly saving challenges with piggy bank and calendar',
      href: '/resources/monthly-saving-challenges-article'
    },
    {
      id: 11,
      titleKey: 'resources.article11.title',
      descriptionKey: 'resources.article11.description',
      category: 'budgeting',
      imageUrl: '/images/articles/1556742049-0cfed4f6a45d',
      imageAlt: 'Impulse buying and psychology of money',
      href: '/resources/impulse-buying-article'
    },
    {
      id: 12,
      titleKey: 'resources.article12.title',
      descriptionKey: 'resources.article12.description',
      category: 'mortgages',
      imageUrl: '/images/articles/1560520653-9e0e4c89eb11',
      imageAlt: 'Mortgage renegotiation and home finance concept',
      href: '/resources/mortgage-renegotiation-article'
    },
    {
      id: 13,
      titleKey: 'resources.article13.title',
      descriptionKey: 'resources.article13.description',
      category: 'debt',
      imageUrl: '/images/articles/1563013544-824ae1b704d3',
      imageAlt: 'Buy now pay later concept with shopping and payment plans',
      href: '/resources/bnpl-article'
    },
    {
      id: 14,
      titleKey: 'resources.article14.title',
      descriptionKey: 'resources.article14.description',
      category: 'technology',
      imageUrl: '/images/articles/1677442136019-21780ecad995',
      imageAlt: 'Artificial intelligence and personal finance technology',
      href: '/resources/ai-finance-article'
    },
    {
      id: 15,
      titleKey: 'resources.article15.title',
      descriptionKey: 'resources.article15.description',
      category: 'taxPlanning',
      imageUrl: '/images/articles/1621761191319-c6fb62004040',
      imageAlt: 'Cryptocurrency taxation and compliance guide',
      href: '/resources/crypto-tax-guide-article'
    },
    {
      id: 16,
      titleKey: 'resources.article16.title',
      descriptionKey: 'resources.article16.description',
      category: 'investment',
      imageUrl: '/images/articles/1526304640581-d334cdbbf45e',
      imageAlt: 'Small stack of dollar bills with financial growth concept and investment planning',
      href: '/resources/investing-with-100-article'
    },
    {
      id: 17,
      titleKey: 'resources.article17.title',
      descriptionKey: 'resources.article17.description',
      category: 'investment',
      imageUrl: '/images/articles/1642543492481-44e81e3914a7',
      imageAlt: 'Modern ETF and index fund investment portfolio with digital charts and analytics',
      href: '/resources/etf-index-funds-article'
    },
    {
      id: 18,
      titleKey: 'resources.article18.title',
      descriptionKey: 'resources.article18.description',
      category: 'security',
      imageUrl: '/images/articles/1563013544-824ae1b704d3',
      imageAlt: 'Digital security and financial scam protection',
      href: '/resources/digital-scams-article'
    },
    {
      id: 19,
      titleKey: 'resources.article19.title',
      descriptionKey: 'resources.article19.description',
      category: 'budgeting',
      imageUrl: '/images/articles/1554224154-22dec7ec8818',
      imageAlt: 'Self-employed finances and budgeting concept',
      href: '/resources/self-employed-finances-article'
    },
    {
      id: 20,
      titleKey: 'resources.article20.title',
      descriptionKey: 'resources.article20.description',
      category: 'investment',
      imageUrl: '/images/articles/1441974231531-c6227db76b6e',
      imageAlt: 'ESG sustainable investment and green finance concept',
      href: '/resources/esg-greenwashing-article'
    },
    {
      id: 21,
      titleKey: 'resources.article21.title',
      descriptionKey: 'resources.article21.description',
      category: 'investment',
      imageUrl: '/images/articles/1460925895917-afdab827c52f',
      imageAlt: 'Financial charts and economic data showing inflation protection strategies',
      href: '/resources/inflation-savings-strategies-article'
    },
    {
      id: 22,
      titleKey: 'resources.article22.title',
      descriptionKey: 'resources.article22.description',
      category: 'career',
      imageUrl: '/images/articles/1507003211169-0a1dd7228f2d',
      imageAlt: 'Professional meeting discussing salary negotiation and career advancement',
      href: '/resources/salary-negotiation-article'
    },
    {
      id: 23,
      titleKey: 'resources.article23.title',
      descriptionKey: 'resources.article23.description',
      category: 'taxPlanning',
      imageUrl: '/images/articles/1488590528505-98d2b5aba04b',
      imageAlt: 'Digital nomad working remotely with laptop and global tax planning concept',
      href: '/resources/digital-nomad-tax-guide-article'
    },
    {
      id: 24,
      titleKey: 'resources.article24.title',
      descriptionKey: 'resources.article24.description',
      category: 'retirement',
      imageUrl: '/images/articles/1559526324-4b87b5e36e44',
      imageAlt: 'Financial independence and realistic retirement planning concept',
      href: '/resources/financial-independence-realistic-plan-article'
    },
    {
      id: 25,
      titleKey: 'resources.article25.title',
      descriptionKey: 'resources.article25.description',
      category: 'technology',
      imageUrl: '/images/articles/1551836022-deb4988cc6c0',
      imageAlt: 'Robo advisors vs human financial advisors comparison',
      href: '/resources/roboadvisors-vs-human-advisors-article'
    },
    {
      id: 26,
      titleKey: 'resources.article26.title',
      descriptionKey: 'resources.article26.description',
      category: 'budgeting',
      imageUrl: '/images/articles/1558618666-fcd25c85cd64',
      imageAlt: 'Utility bills and home expenses optimization concept',
      href: '/resources/utility-bills-article'
    },
    {
      id: 27,
      titleKey: 'resources.article27.title',
      descriptionKey: 'resources.article27.description',
      category: 'taxPlanning',
      imageUrl: '/images/articles/1587614382346-4ec70e388b28',
      imageAlt: 'Tax calculation and financial planning documents concept',
      href: '/resources/irpf-guide-article'
    },
    {
      id: 28,
      titleKey: 'resources.article28.title',
      descriptionKey: 'resources.article28.description',
      category: 'investment',
      imageUrl: '/images/articles/1579532537598-459ecdaf39cc',
      imageAlt: 'Young professional starting investment journey with laptop showing financial dashboard and growth charts',
      href: '/resources/start-investing-article'
    },
    {
      id: 29,
      titleKey: 'resources.article29.title',
      descriptionKey: 'resources.article29.description',
      category: 'budgeting',
      imageUrl: '/images/articles/1476703993599-0035a21b17a9',
      imageAlt: 'Happy family with newborn baby and financial planning documents showing preparation for children expenses',
      href: '/resources/financial-planning-before-children-article'
    },
    {
      id: 30,
      titleKey: 'resources.article30.title',
      descriptionKey: 'resources.article30.description',
      category: 'budgeting',
      imageUrl: '/images/articles/1579621970563-ebec7560ff3e',
      imageAlt: 'Saving money on low salary budgeting strategies',
      href: '/resources/low-salary-saving-strategies-article'
    },
    {
      id: 31,
      titleKey: 'resources.article31.title',
      descriptionKey: 'resources.article31.description',
      category: 'investment',
      imageUrl: '/images/articles/1611974789855-9c2a0a7236a3',
      imageAlt: 'Trading losses and market volatility concept',
      href: '/resources/why-traders-lose-money-article'
    },
    {
      id: 32,
      titleKey: 'resources.article32.title',
      descriptionKey: 'resources.article32.description',
      category: 'investment',
      imageUrl: '/images/articles/1579621970795-87facc2f976d',
      imageAlt: 'Person holding empty wallet representing the invisible cost of not investing and lost financial opportunities',
      href: '/resources/real-cost-never-investing-article'
    },
    {
      id: 33,
      titleKey: 'resources.article33.title',
      descriptionKey: 'resources.article33.description',
      category: 'budgeting',
      imageUrl: '/images/articles/1607863680198-23d4b2565df0',
      imageAlt: 'Money leaking through holes representing invisible expenses draining your budget',
      href: '/resources/invisible-expenses-article'
    },
    {
      id: 34,
      titleKey: 'resources.article34.title',
      descriptionKey: 'resources.article34.description',
      category: 'investment',
      imageUrl: '/images/articles/1635070041078-e363dbe005cb',
      imageAlt: 'AI-powered investment strategies and automated trading algorithms',
      href: '/resources/ai-investment-article'
    },
    {
      id: 35,
      titleKey: 'resources.article35.title',
      descriptionKey: 'resources.article35.description',
      category: 'budgeting',
      imageUrl: '/images/articles/1483985988355-763728e1935b',
      imageAlt: 'Luxury shopping bags and lifestyle representing lifestyle inflation and increased spending',
      href: '/resources/lifestyle-inflation-article'
    },
    {
      id: 36,
      titleKey: 'resources.article36.title',
      descriptionKey: 'resources.article36.description',
      category: 'budgeting',
      imageUrl: '/images/articles/1515488042361-ee00e0ddd4e4',
      imageAlt: 'Young couple planning finances with calculator and documents preparing for future parenthood and family expenses',
      href: '/resources/preparing-finances-before-parenthood-article'
    },
    {
      id: 37,
      titleKey: 'resources.article37.title',
      descriptionKey: 'resources.article37.description',
      category: 'investment',
      imageUrl: '/images/articles/1551288049-bebda4e38f71',
      imageAlt: 'Investment strategy comparison with charts showing Dollar Cost Averaging vs Lump Sum investing analysis',
      href: '/resources/dca-vs-lump-sum-article'
    },
    {
      id: 38,
      titleKey: 'resources.article38.title',
      descriptionKey: 'resources.article38.description',
      category: 'retirement',
      imageUrl: '/images/articles/1473186578172-c141e6798cf4',
      imageAlt: 'Relaxed retirement planning concept with calculator and coffee',
      href: '/resources/how-much-to-retire-article'
    },
    {
      id: 39,
      titleKey: 'resources.article39.title',
      descriptionKey: 'resources.article39.description',
      category: 'budgeting',
      imageUrl: '/images/articles/1516589178581-6cd7833ae3b2',
      imageAlt: 'Happy couple managing finances together with financial documents and laptop',
      href: '/resources/couples-finance-article'
    },
    {
      id: 40,
      titleKey: 'resources.article40.title',
      descriptionKey: 'resources.article40.description',
      category: 'career',
      imageUrl: '/images/articles/1523240795612-9a054b0db644',
      imageAlt: 'Young professional celebrating first salary success with laptop and financial documents',
      href: '/resources/first-salary-article'
    },
    {
      id: 41,
      titleKey: 'resources.article41.title',
      descriptionKey: 'resources.article41.description',
      category: 'technology',
      imageUrl: '/images/articles/1526628953301-3e589a6a8b74',
      imageAlt: 'Automated financial system with digital charts and automated transfers concept',
      href: '/resources/automate-finances-article'
    },
    {
      id: 42,
      titleKey: 'resources.article42.title',
      descriptionKey: 'resources.article42.description',
      category: 'budgeting',
      imageUrl: '/images/articles/1551836022-d5d88e9218df',
      imageAlt: 'Person improving 1% each day with progress tracking and incremental growth concept',
      href: '/resources/one-percent-rule-article'
    },
    {
      id: 43,
      titleKey: 'resources.article43.title',
      descriptionKey: 'resources.article43.description',
      category: 'investment',
      imageUrl: '/images/articles/1559589689-577aabd1db4f',
      imageAlt: 'Dividend reinvestment concept with growing investment portfolio and compound returns visualization',
      href: '/resources/dividend-reinvestment-article'
    },
    {
      id: 44,
      titleKey: 'resources.article44.title',
      descriptionKey: 'resources.article44.description',
      category: 'career',
      imageUrl: '/images/articles/1450101499163-c8848c66ca85',
      imageAlt: 'Professional negotiating salary with confidence and financial documents showing career growth',
      href: '/resources/salary-negotiation-worth-article'
    },
    {
      id: 45,
      titleKey: 'resources.article45.title',
      descriptionKey: 'resources.article45.description',
      category: 'investment',
      imageUrl: '/images/articles/1559526324-593bc073d938',
      imageAlt: 'Person overcoming fear of investing with financial education and strategy planning',
      href: '/resources/fear-of-investing-article'
    },
    {
      id: 46,
      titleKey: 'resources.article46.title',
      descriptionKey: 'resources.article46.description',
      category: 'career',
      imageUrl: '/images/articles/1454165804606-c3d57bc86b40',
      imageAlt: 'Professional preparing to quit job with financial planning and career transition concept',
      href: '/resources/before-quitting-job-article'
    }
  ], []);

  // Videos data structure
  const videos = useMemo(() => [
    {
      id: 1,
      titleKey: 'resources.videos.video1.title',
      descriptionKey: 'resources.videos.video1.description',
      category: 'economy',
      videoUrl: 'https://www.youtube.com/watch?v=UMAELCrJxt0',
      embedUrl: 'https://www.youtube.com/embed/UMAELCrJxt0',
      thumbnail: 'https://img.youtube.com/vi/UMAELCrJxt0/maxresdefault.jpg',
      duration: '8:00',
      platform: 'youtube'
    }
  ], []);

  // Calculators data structure
  const calculators = useMemo(() => [
    {
      id: 1,
      titleKey: 'tools.compound.title',
      descriptionKey: 'tools.compound.description',
      category: 'investment',
      href: '/tools/compound-interest',
      icon: 'LineChart'
    },
    {
      id: 2,
      titleKey: 'tools.salary.title',
      descriptionKey: 'tools.salary.description',
      category: 'budgeting',
      href: '/tools/net-salary',
      icon: 'DollarSign'
    },
    {
      id: 3,
      titleKey: 'tools.savings.title',
      descriptionKey: 'tools.savings.description',
      category: 'budgeting',
      href: '/tools/monthly-savings-calculator',
      icon: 'PiggyBank'
    },
    {
      id: 4,
      titleKey: 'tools.mortgage.title',
      descriptionKey: 'tools.mortgage.description',
      category: 'mortgages',
      href: '/tools/mortgage-calculator',
      icon: 'Home'
    }
  ], []);

  // Filtered articles based on search and category
  const filteredArticles = useMemo(() => {
    return articles.filter(article => {
      const title = t(article.titleKey);
      const description = t(article.descriptionKey);
      const category = t(`resources.categories.${article.category}`);
      
      const matchesSearch = matchesSearchTerm(title, searchQuery) || 
                           matchesSearchTerm(description, searchQuery) || 
                           matchesSearchTerm(category, searchQuery);
      
      const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [articles, searchQuery, selectedCategory, t]);

  // Filtered videos based on search and category
  const filteredVideos = useMemo(() => {
    return videos.filter(video => {
      const title = t(video.titleKey);
      const description = t(video.descriptionKey);
      const category = t(`resources.categories.${video.category}`);
      
      const matchesSearch = matchesSearchTerm(title, searchQuery) || 
                           matchesSearchTerm(description, searchQuery) || 
                           matchesSearchTerm(category, searchQuery);
      
      const matchesCategory = selectedCategory === 'all' || video.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [videos, searchQuery, selectedCategory, t]);

  // Filtered calculators based on search and category
  const filteredCalculators = useMemo(() => {
    return calculators.filter(calculator => {
      const title = t(calculator.titleKey);
      const description = t(calculator.descriptionKey);
      const category = t(`resources.categories.${calculator.category}`);
      
      const matchesSearch = matchesSearchTerm(title, searchQuery) || 
                           matchesSearchTerm(description, searchQuery) || 
                           matchesSearchTerm(category, searchQuery);
      
      const matchesCategory = selectedCategory === 'all' || calculator.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [calculators, searchQuery, selectedCategory, t]);

  // Categories for filter dropdown
  const categories = [
    { value: 'all', label: t('resources.search.allCategories') },
    { value: 'investment', label: t('resources.categories.investment') },
    { value: 'retirement', label: t('resources.categories.retirement') },
    { value: 'taxPlanning', label: t('resources.categories.taxPlanning') },
    { value: 'budgeting', label: t('resources.categories.budgeting') },
    { value: 'mortgages', label: t('resources.categories.mortgages') },
    { value: 'debt', label: t('resources.categories.debt') },
    { value: 'technology', label: t('resources.categories.technology') },
    { value: 'security', label: t('resources.categories.security') },
    { value: 'economy', label: t('resources.categories.economy') },
    { value: 'career', label: t('resources.categories.career') }
  ];

  const clearSearch = () => {
    setSearchQuery('');
    setSelectedCategory('all');
  };

  const handleLoadMoreArticles = () => {
    setArticlesToShow(prev => prev + 9);
  };

  const handleLoadMoreVideos = () => {
    setVideosToShow(prev => prev + 9);
  };

  const handleLoadMoreCalculators = () => {
    setCalculatorsToShow(prev => prev + 9);
  };

  const handleShowLessArticles = () => {
    setArticlesToShow(9);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShowLessVideos = () => {
    setVideosToShow(9);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShowLessCalculators = () => {
    setCalculatorsToShow(9);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Reset pagination when search or category changes
  const resetPagination = () => {
    setArticlesToShow(9);
    setVideosToShow(9);
    setCalculatorsToShow(9);
  };

  // Effect to reset pagination when search or category changes
  useEffect(() => {
    resetPagination();
  }, [searchQuery, selectedCategory]);

  return (
    <section className="bg-white py-16 fade-in">
      {seoContent}
      <div className="container mx-auto px-4 md:px-6">
        <h2 className="text-2xl md:text-3xl font-bold text-neutral-800 text-center mb-4">
          {t('resources.title')}
        </h2>
        <p className="text-neutral-600 text-center max-w-2xl mx-auto mb-8">
          {t('resources.subtitle')}
        </p>

        {/* Search and Filter Section */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 h-4 w-4" />
              <Input
                type="text"
                placeholder={t('resources.search.placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10 h-12 border-neutral-200 focus:border-[#EA580C] focus:ring-[#EA580C]"
              />
              {(searchQuery || selectedCategory !== 'all') && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Category Filter */}
            <div className="md:w-48">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="h-12 border-neutral-200 focus:border-[#EA580C] focus:ring-[#EA580C]">
                  <Filter className="h-4 w-4 mr-2 text-neutral-400" />
                  <SelectValue placeholder={t('resources.search.filterByCategory')} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.value} value={category.value}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Results Count */}
          {activeTab === 'articles' && (
            <div className="text-center text-sm text-neutral-600 mb-4">
              {`${filteredArticles.length} de ${articles.length} artículos`}
            </div>
          )}
          {activeTab === 'videos' && (
            <div className="text-center text-sm text-neutral-600 mb-4">
              {`${filteredVideos.length} de ${videos.length} videos`}
            </div>
          )}
          {activeTab === 'calculators' && (
            <div className="text-center text-sm text-neutral-600 mb-4">
              {`${filteredCalculators.length} de ${calculators.length} calculadoras`}
            </div>
          )}
        </div>

        <Tabs defaultValue="articles" className="w-full" onValueChange={setActiveTab}>
          <div className="flex justify-center mb-8">
            <TabsList>
              <TabsTrigger value="articles">{t('resources.tabs.articles')}</TabsTrigger>
              <TabsTrigger value="videos">{t('resources.tabs.videos')}</TabsTrigger>
              <TabsTrigger value="calculators">{t('resources.tabs.calculators')}</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="articles" className="pt-2">
            {filteredArticles.length === 0 ? (
              <div className="text-center py-12">
                <Search className="h-16 w-16 text-neutral-300 mx-auto mb-4" />
                <h3 className="font-medium text-xl text-neutral-600 mb-2">
                  {t('resources.search.noResults')}
                </h3>
                <p className="text-neutral-500 mb-4">
                  {t('resources.search.noResultsDescription')}
                </p>
                <Button onClick={clearSearch} variant="outline" className="border-[#EA580C] text-[#EA580C] hover:bg-[#EA580C] hover:text-white">
                  <X className="h-4 w-4 mr-2" />
                  {t('resources.search.clearFilters')}
                </Button>
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredArticles.slice(0, articlesToShow).map((article) => (
                  <Card key={article.id} className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden relative">
                    {article.isPremium && (
                      <div className="absolute top-3 right-3 z-10">
                        <span className="bg-gradient-to-r from-[#EA580C] to-[#FB923C] text-white text-xs font-medium px-2 py-1 rounded-full flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                          </svg>
                          Premium
                        </span>
                      </div>
                    )}
                    {article.isPremium ? (
                      <picture>
                        <source
                          type="image/avif"
                          srcSet={`${article.imageUrl}-500w.avif 500w, ${article.imageUrl}-800w.avif 800w`}
                          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                        />
                        <source
                          type="image/webp"
                          srcSet={`${article.imageUrl}-500w.webp 500w, ${article.imageUrl}-800w.webp 800w`}
                          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                        />
                        <img
                          src={`${article.imageUrl}-500w.jpg`}
                          srcSet={`${article.imageUrl}-500w.jpg 500w, ${article.imageUrl}-800w.jpg 800w`}
                          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                          alt={article.imageAlt}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-48 object-cover filter grayscale cursor-not-allowed"
                        />
                      </picture>
                    ) : (
                      <LanguageLink to={article.href}>
                        <picture>
                          <source
                            type="image/avif"
                            srcSet={`${article.imageUrl}-500w.avif 500w, ${article.imageUrl}-800w.avif 800w`}
                            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                          />
                          <source
                            type="image/webp"
                            srcSet={`${article.imageUrl}-500w.webp 500w, ${article.imageUrl}-800w.webp 800w`}
                            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                          />
                          <img
                            src={`${article.imageUrl}-500w.jpg`}
                            srcSet={`${article.imageUrl}-500w.jpg 500w, ${article.imageUrl}-800w.jpg 800w`}
                            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                            alt={article.imageAlt}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                          />
                        </picture>
                      </LanguageLink>
                    )}
                    <CardContent className="p-6">
                      <span className="inline-block px-2 py-1 bg-neutral-100 text-neutral-600 text-xs rounded-full mb-3">
                        {t(`resources.categories.${article.category}`)}
                      </span>
                      <h3 className="font-semibold text-xl mb-2">{t(article.titleKey)}</h3>
                      <p className="text-neutral-600 mb-4">{t(article.descriptionKey)}</p>
                      {article.isPremium ? (
                        <div className="inline-block text-neutral-400 font-medium cursor-not-allowed flex items-center">
                          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                          </svg>
                          {t('resources.article.read')} <ArrowRight className="inline h-4 w-4 ml-1" />
                        </div>
                      ) : (
                        <LanguageLink to={article.href}>
                          <div className="inline-block text-[#EA580C] font-medium hover:underline cursor-pointer">
                            {t('resources.article.read')} <ArrowRight className="inline h-4 w-4 ml-1" />
                          </div>
                        </LanguageLink>
                      )}
                    </CardContent>
                  </Card>
                ))}
                </div>
                
                {/* Load More / Show Less Buttons for Articles */}
                <div className="flex justify-center gap-4 mt-8">
                  {filteredArticles.length > articlesToShow && (
                    <Button 
                      onClick={handleLoadMoreArticles}
                      className="bg-[#EA580C] hover:bg-[#DC2626] text-white px-8 py-3 rounded-lg font-medium transition-colors"
                    >
                      {t('resources.loadMore')}
                    </Button>
                  )}
                  {articlesToShow > 9 && (
                    <Button 
                      onClick={handleShowLessArticles}
                      variant="outline"
                      className="border-[#EA580C] text-[#EA580C] hover:bg-[#EA580C] hover:text-white px-8 py-3 rounded-lg font-medium transition-colors"
                    >
                      {t('resources.showLess')}
                    </Button>
                  )}
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="videos">
            {filteredVideos.length === 0 ? (
              <div className="text-center py-12">
                <Film className="h-16 w-16 text-neutral-300 mx-auto mb-4" />
                <h3 className="font-medium text-xl text-neutral-600 mb-2">
                  {searchQuery || selectedCategory !== 'all' ? t('resources.search.noResults') : t('resources.videos.comingSoon')}
                </h3>
                <p className="text-neutral-500 mb-4">
                  {searchQuery || selectedCategory !== 'all' ? t('resources.search.noResultsDescription') : t('resources.videos.inProgress')}
                </p>
                {(searchQuery || selectedCategory !== 'all') && (
                  <Button onClick={clearSearch} variant="outline" className="border-[#EA580C] text-[#EA580C] hover:bg-[#EA580C] hover:text-white">
                    <X className="h-4 w-4 mr-2" />
                    {t('resources.search.clearFilters')}
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredVideos.slice(0, videosToShow).map((video) => (
                  <Card key={video.id} className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
                    <div className="relative">
                      <img
                        src={video.thumbnail}
                        alt={t(video.titleKey)}
                        className="w-full h-48 object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 hover:bg-opacity-30 transition-all">
                        <button
                          onClick={() => window.open(video.videoUrl, '_blank')}
                          className="bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-3 transition-all transform hover:scale-110"
                          aria-label="Reproducir video"
                        >
                          <svg className="w-6 h-6 text-[#EA580C] ml-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M8 5v10l8-5-8-5z"/>
                          </svg>
                        </button>
                      </div>
                      <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                        {video.duration}
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <span className="inline-block px-2 py-1 bg-neutral-100 text-neutral-600 text-xs rounded-full mb-3">
                        {t(`resources.categories.${video.category}`)}
                      </span>
                      <h3 className="font-semibold text-xl mb-2">{t(video.titleKey)}</h3>
                      <p className="text-neutral-600 mb-4">{t(video.descriptionKey)}</p>
                      <button
                        onClick={() => window.open(video.videoUrl, '_blank')}
                        className="inline-block text-[#EA580C] font-medium hover:underline cursor-pointer"
                      >
                        Ver Video <ArrowRight className="inline h-4 w-4 ml-1" />
                      </button>
                    </CardContent>
                  </Card>
                ))}
                </div>
                
                {/* Load More / Show Less Buttons for Videos */}
                <div className="flex justify-center gap-4 mt-8">
                  {filteredVideos.length > videosToShow && (
                    <Button 
                      onClick={handleLoadMoreVideos}
                      className="bg-[#EA580C] hover:bg-[#DC2626] text-white px-8 py-3 rounded-lg font-medium transition-colors"
                    >
                      {t('resources.loadMore')}
                    </Button>
                  )}
                  {videosToShow > 9 && (
                    <Button 
                      onClick={handleShowLessVideos}
                      variant="outline"
                      className="border-[#EA580C] text-[#EA580C] hover:bg-[#EA580C] hover:text-white px-8 py-3 rounded-lg font-medium transition-colors"
                    >
                      {t('resources.showLess')}
                    </Button>
                  )}
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="calculators">
            {filteredCalculators.length === 0 ? (
              <div className="text-center py-12">
                <LineChart className="h-16 w-16 text-neutral-300 mx-auto mb-4" />
                <h3 className="font-medium text-xl text-neutral-600 mb-2">
                  {t('resources.search.noResults')}
                </h3>
                <p className="text-neutral-500 mb-4">
                  {t('resources.search.noResultsDescription')}
                </p>
                <Button onClick={clearSearch} variant="outline" className="border-[#EA580C] text-[#EA580C] hover:bg-[#EA580C] hover:text-white">
                  <X className="h-4 w-4 mr-2" />
                  {t('resources.search.clearFilters')}
                </Button>
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredCalculators.slice(0, calculatorsToShow).map((calculator) => {
                  const IconComponent = {
                    LineChart,
                    DollarSign,
                    PiggyBank,
                    Home
                  }[calculator.icon] || LineChart;
                  
                  return (
                    <Card
                      key={calculator.id}
                      className="bg-white rounded-xl border border-neutral-200 shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => window.location.href = calculator.href}
                    >
                      <div className="w-12 h-12 rounded-full bg-[#FB923C]/20 flex items-center justify-center mb-4">
                        <IconComponent className="h-6 w-6 text-[#EA580C]" />
                      </div>
                      <h3 className="font-semibold text-xl mb-2">{t(calculator.titleKey)}</h3>
                      <p className="text-neutral-600">{t(calculator.descriptionKey)}</p>
                    </Card>
                  );
                })}
                </div>
                
                {/* Load More / Show Less Buttons for Calculators */}
                <div className="flex justify-center gap-4 mt-8">
                  {filteredCalculators.length > calculatorsToShow && (
                    <Button 
                      onClick={handleLoadMoreCalculators}
                      className="bg-[#EA580C] hover:bg-[#DC2626] text-white px-8 py-3 rounded-lg font-medium transition-colors"
                    >
                      {t('resources.loadMore')}
                    </Button>
                  )}
                  {calculatorsToShow > 9 && (
                    <Button 
                      onClick={handleShowLessCalculators}
                      variant="outline"
                      className="border-[#EA580C] text-[#EA580C] hover:bg-[#EA580C] hover:text-white px-8 py-3 rounded-lg font-medium transition-colors"
                    >
                      {t('resources.showLess')}
                    </Button>
                  )}
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default ResourcesPage;
